import { typeahead } from "@mirage/service-typeahead-search/service/types";
import { doesNotContainFilterBindingPrefix } from "./predicate-factory";

const doesNotContainFilterBindingPrefixPredicate =
  doesNotContainFilterBindingPrefix();

type SectionCondition = {
  mode: typeahead.Mode;
  hasQuery: boolean;
  section: typeahead.SectionId | undefined;
  dynamicCallback?: (
    query: string,
    mode: typeahead.Mode,
    config: typeahead.Config,
  ) => boolean;
  dynamicSection?: typeahead.SectionId | undefined;
};

const DEFAULT_TYPING_FIND = {
  mode: typeahead.Mode.Default,
  hasQuery: true,
  section: typeahead.SectionId.Find,
};

const DEFAULT_TYPING_FIND_WITH_FILTER = {
  ...DEFAULT_TYPING_FIND,
  dynamicCallback: (
    query: string,
    mode: typeahead.Mode,
    config: typeahead.Config,
  ) => {
    return !(
      mode === typeahead.Mode.Default &&
      doesNotContainFilterBindingPrefixPredicate(query, mode, {
        config,
        cacheAnalysis: {
          hasRecentQueries: false,
        },
      })
    );
  },
  dynamicSection: undefined,
};

const DEFAULT_TYPING_ASK = {
  mode: typeahead.Mode.Default,
  hasQuery: true,
  section: typeahead.SectionId.Ask,
};

const DEFAULT_NULL_STATE_ASK = {
  mode: typeahead.Mode.Default,
  hasQuery: false,
  section: typeahead.SectionId.Ask,
};

const DEFAULT_NULL_STATE_FIND = {
  mode: typeahead.Mode.Default,
  hasQuery: false,
  section: typeahead.SectionId.Find,
};

const FIND_RECOMMENDATION_CONDITIONS: SectionCondition[] = [
  DEFAULT_NULL_STATE_FIND,
  DEFAULT_TYPING_FIND,
  {
    mode: typeahead.Mode.Find,
    hasQuery: false,
    section: typeahead.SectionId.Recommended,
  },
];

const FIND_RECENT_CONDITIONS: SectionCondition[] = [
  {
    mode: typeahead.Mode.Default,
    hasQuery: false,
    section: typeahead.SectionId.Find,
  },
  {
    mode: typeahead.Mode.Default,
    hasQuery: true,
    section: typeahead.SectionId.Find,
  },
  DEFAULT_TYPING_FIND,
  {
    mode: typeahead.Mode.Find,
    hasQuery: false,
    section: typeahead.SectionId.Recent,
  },
];

const ASK_CONDITIONS: SectionCondition[] = [
  DEFAULT_NULL_STATE_ASK,
  DEFAULT_TYPING_ASK,
  {
    mode: typeahead.Mode.Ask,
    hasQuery: false,
    section: typeahead.SectionId.Suggested,
  },
];

const RECENT_CHAT_CONDITIONS: SectionCondition[] = [
  DEFAULT_TYPING_ASK,
  {
    mode: typeahead.Mode.Ask,
    hasQuery: false,
    section: typeahead.SectionId.Recent,
  },
  {
    mode: typeahead.Mode.Default,
    hasQuery: false,
    section: typeahead.SectionId.Ask,
  },
  {
    mode: typeahead.Mode.Default,
    hasQuery: true,
    section: typeahead.SectionId.Ask,
  },
];

const sectionMappings: Record<typeahead.ResultType, SectionCondition[]> = {
  [typeahead.ResultType.SearchResult]: [],
  [typeahead.ResultType.Stack]: FIND_RECOMMENDATION_CONDITIONS,
  [typeahead.ResultType.StackItem]: FIND_RECOMMENDATION_CONDITIONS,
  [typeahead.ResultType.Recommendation]: FIND_RECOMMENDATION_CONDITIONS,
  [typeahead.ResultType.SuggestedQuery]: [DEFAULT_TYPING_FIND_WITH_FILTER],
  [typeahead.ResultType.AskSuggestion]: ASK_CONDITIONS,
  [typeahead.ResultType.PreviousQuery]: FIND_RECENT_CONDITIONS,
  [typeahead.ResultType.DraftWithSource]: [
    {
      mode: typeahead.Mode.Write,
      hasQuery: false,
      section: typeahead.SectionId.DraftFromSource,
    },
  ],
  [typeahead.ResultType.DraftFromTemplate]: [
    {
      mode: typeahead.Mode.Write,
      hasQuery: false,
      section: typeahead.SectionId.DraftFromTemplate,
    },
  ],
  [typeahead.ResultType.DraftFromTone]: [
    {
      mode: typeahead.Mode.Write,
      hasQuery: false,
      section: typeahead.SectionId.DraftFromTone,
    },
  ],
  [typeahead.ResultType.AskUserQuery]: [],
  [typeahead.ResultType.DesktopFile]: FIND_RECOMMENDATION_CONDITIONS,
  [typeahead.ResultType.DesktopApplication]: FIND_RECOMMENDATION_CONDITIONS,
  [typeahead.ResultType.RecentChat]: RECENT_CHAT_CONDITIONS,
  [typeahead.ResultType.OrganizeUserQuery]: [],
  [typeahead.ResultType.WriteUserQuery]: [],
  [typeahead.ResultType.MathCalculation]: [],
  [typeahead.ResultType.SearchFilter]: [],
  [typeahead.ResultType.URLShortcut]: [],
  [typeahead.ResultType.PeopleFilter]: [DEFAULT_TYPING_FIND],
  [typeahead.ResultType.PeopleRecommendation]: [DEFAULT_TYPING_FIND],
  [typeahead.ResultType.CalendarEvent]: [],
  [typeahead.ResultType.MyProfileSuggestion]: FIND_RECOMMENDATION_CONDITIONS,

  [typeahead.ResultType.Skeleton]: FIND_RECOMMENDATION_CONDITIONS,
  [typeahead.ResultType.LoadingCompletion]: [],
};

/**
 * Gets the appropriate section ID for a result based on its type, the current mode,
 * and the query state.
 */
export function getResultSection(
  resultType: typeahead.ResultType,
  mode: typeahead.Mode,
  query: string,
  config?: typeahead.Config,
): typeahead.SectionId | undefined {
  if (!config?.isOmniboxEnabled) return undefined;

  const hasQuery = query.length > 0;
  const conditions = sectionMappings[resultType];

  const match = (conditions || []).find((condition) => {
    if (typeof condition.dynamicCallback === "function") {
      const isMatch = condition.dynamicCallback(query, mode, config);
      if (isMatch) return condition.dynamicSection;
    }

    return condition.mode === mode && condition.hasQuery === hasQuery;
  });

  return match?.section ?? undefined;
}
